import React, { useEffect, useMemo } from 'react';
import { useQuery } from 'apollo-client-ppc';
import Lozenge from '@atlaskit/lozenge';
import { Field } from '@atlaskit/form';
import Select from '@atlaskit/select';
import Button from '@atlaskit/button';
import { CreditCard, PaymentMethodType } from '@css-xp/graphtypes/src/generated-types-new';
import { fetchPaymentMethodsForOrderReview } from './queries/fetch-available-payment-methods';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { withApollo } from '@css-xp/base';
import {
  CreditCardPaymentMethod,
  PaymentMethod,
  PaymentMethodId,
  PaymentMethodPanel,
} from '@atlassian/commerce-payment-methods';
import './select-payment-method.scss';

const FIELD_NAME = 'selectPaymentMethodForOrder';

export type SelectPaymentMethodProps = {
  transactionAccountId: string;
  fieldName?: string;
  onSelectPaymentMethod?: (paymentMethod: CreditCardPaymentMethod) => void;
  skipLoadingPaymentMethods?: boolean;
  autoSelectedPaymentMethod?: PaymentMethod | undefined | null;
};

const getCardDetails = (paymentMethod: CreditCard) => {
  const creditCard = paymentMethod?.card;
  const expMonth = +(creditCard?.expirationMonth ?? 0);
  const expYear = +(creditCard?.expirationYear ?? 0);
  const card = {
    brand: creditCard?.brand ?? '',
    expMonth,
    expYear,
    last4: creditCard?.last4Numbers ?? '',
    name: creditCard?.cardHolderName ?? '',
  };

  const cardDetails = {
    id: paymentMethod?.id as PaymentMethodId,
    type: paymentMethod?.type,
    card,
    default: paymentMethod?.default,
  } as PaymentMethod;
  return cardDetails;
};

const SelectPaymentMethodImpl: React.FC<SelectPaymentMethodProps> = ({
  transactionAccountId,
  onSelectPaymentMethod,
  fieldName = FIELD_NAME,
  skipLoadingPaymentMethods = false,
  autoSelectedPaymentMethod,
}) => {
  const [isPaymentMethodSelected, setIsPaymentMethodSelected] = React.useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<PaymentMethod | null>(null);
  const { data, loading } = useQuery<
    generatedTypesNew.FetchPaymentMethodsForOrderReviewQuery,
    generatedTypesNew.FetchPaymentMethodsForOrderReviewQueryVariables
  >(fetchPaymentMethodsForOrderReview, {
    variables: {
      id: transactionAccountId,
    },
    skip: skipLoadingPaymentMethods || Boolean(autoSelectedPaymentMethod),
  });

  const allPaymentMethods = data?.transactionAccount.availablePaymentMethods?.nodes;
  const cardPaymentMethods = useMemo(
    () => allPaymentMethods?.filter((paymentMethod) => paymentMethod?.type === PaymentMethodType.CARD) ?? [],
    [allPaymentMethods]
  );

  useEffect(() => {
    if (cardPaymentMethods?.length) {
      const defaultPaymentMethod = cardPaymentMethods?.find((paymentMethod) => !!paymentMethod?.default) as CreditCard;
      if (defaultPaymentMethod) {
        const cardDetails = getCardDetails(defaultPaymentMethod);
        setSelectedPaymentMethod(cardDetails as PaymentMethod);
        setIsPaymentMethodSelected(true);
        onSelectPaymentMethod?.(cardDetails as CreditCardPaymentMethod);
      }
    }
  }, [cardPaymentMethods]);

  const cardNumbers = useMemo(() => {
    return cardPaymentMethods.map((paymentMethod: any) => {
      const creditCard = paymentMethod as CreditCard;
      const cardDetails = getCardDetails(creditCard);
      return {
        label: (
          <div className="payment-method-option-menu" data-testid={`payment-method-${creditCard.id}`}>
            <PaymentMethodPanel paymentMethod={cardDetails} />
            {paymentMethod && paymentMethod?.default && (
              <Lozenge appearance="default" testId={`default-status-lozenge-${creditCard.id}`}>
                Default
              </Lozenge>
            )}
          </div>
        ),
        value: creditCard.id,
        paymentMethod: cardDetails,
      };
    });
  }, [allPaymentMethods]);

  return (
    <div
      className="ppc-order-review-select-payment-method-container"
      data-testid="ppc-order-review-select-payment-method"
    >
      {autoSelectedPaymentMethod ? (
        <div className="ppc-selected-payment-method-container">
          <div className="credit-card-container">
            <h5 className="credit-card-header">Credit card</h5>
            <PaymentMethodPanel paymentMethod={autoSelectedPaymentMethod} />
          </div>
        </div>
      ) : (
        <Field label="You're paying by" name={fieldName} isRequired>
          {() =>
            isPaymentMethodSelected && selectedPaymentMethod ? (
              <div className="ppc-selected-payment-method-container">
                <div className="credit-card-container">
                  <p className="credit-card-header">Credit card</p>
                  <PaymentMethodPanel paymentMethod={selectedPaymentMethod} />
                </div>
                <Button
                  appearance="link"
                  testId="ppc-change-order-payment-method"
                  onClick={() => {
                    setIsPaymentMethodSelected(false);
                  }}
                >
                  Change
                </Button>
              </div>
            ) : (
              <Select
                classNamePrefix={'ppc-select-payment-method-order-review'}
                placeholder="Select payment method..."
                isLoading={loading}
                options={cardNumbers}
                onChange={(selectedPaymentMethod) => {
                  const selected = (selectedPaymentMethod as unknown) as {
                    label: typeof PaymentMethodPanel;
                    value: string;
                    paymentMethod: PaymentMethod;
                  };
                  if (selected && selected.value) {
                    setSelectedPaymentMethod(selected.paymentMethod);

                    setIsPaymentMethodSelected(true);
                    onSelectPaymentMethod && onSelectPaymentMethod(selected.paymentMethod as CreditCardPaymentMethod);
                  }
                }}
              />
            )
          }
        </Field>
      )}
    </div>
  );
};

export const SelectPaymentMethod = withApollo(SelectPaymentMethodImpl);
